// Admin master: conta única da FSC no provedor fiscal (ACBr API) e as empresas
// dos clientes cadastradas nela. As credenciais ficam só no .env do servidor —
// aqui aparece apenas SE estão configuradas, nunca o valor.
import React, { useCallback, useEffect, useState } from "react";
import { fiscal } from "../api";
import { Aviso, Botao, Cartao, Tabela } from "../components/ui";
import { dataBR, mascaraDocumento } from "../utils";

const SITUACAO = {
  sincronizada: ["Ativa", "bg-emerald-100 text-emerald-800"],
  pendente: ["Pendente", "bg-amber-100 text-amber-800"],
  erro: ["Erro", "bg-red-100 text-red-700"],
};
const CERT = {
  ativo: ["Ativo", "text-emerald-700"],
  vence_em_breve: ["Vence em breve", "text-amber-700"],
  expirado: ["Expirado", "text-red-700"],
  ausente: ["Não enviado", "text-gray-500"],
};
const AMBIENTE = { homologacao: "Homologação", producao: "Produção" };

// Tabelas públicas NCM e CEST usadas na busca do cadastro de produtos
function TabelasFiscais() {
  const [tabs, setTabs] = useState([]);
  const [ocupado, setOcupado] = useState(null);
  const [msg, setMsg] = useState(null);
  const carregar = useCallback(() => fiscal.adminTabelas().then(setTabs).catch((e) => setMsg({ tipo: "erro", texto: e.message })), []);
  useEffect(() => { carregar(); }, [carregar]);
  const atualizar = async (tipo) => {
    setOcupado(tipo);
    setMsg(null);
    try {
      const r = await fiscal.adminAtualizarTabela(tipo);
      setMsg({ tipo: "sucesso", texto: `Tabela ${tipo.toUpperCase()} atualizada: ${r.total} códigos.` });
    } catch (e) {
      setMsg({ tipo: "erro", texto: e.message });
    } finally {
      setOcupado(null);
      carregar();
    }
  };
  const importar = async (arquivo) => {
    if (!arquivo) return;
    setOcupado("cest");
    setMsg(null);
    try {
      const r = await fiscal.adminImportarCest(arquivo);
      setMsg({ tipo: "sucesso", texto: `Tabela CEST importada: ${r.total} códigos.` });
    } catch (e) {
      setMsg({ tipo: "erro", texto: e.message });
    } finally {
      setOcupado(null);
      carregar();
    }
  };
  const nomes = { ncm: "NCM (Siscomex)", cest: "CEST (Convênio ICMS 142/18)" };
  return (
    <Cartao titulo="Tabelas NCM e CEST (cadastro de produtos)">
      <p className="mb-3 text-sm text-gray-600">Atualizam sozinhas a cada 30 dias. Use o botão se a Receita/CONFAZ publicar mudanças.</p>
      {msg && <Aviso tipo={msg.tipo}>{msg.texto}</Aviso>}
      <div className="grid gap-3 sm:grid-cols-2">
        {tabs.map((t) => (
          <div key={t.tabela} className="rounded-lg border border-gray-200 p-3 text-sm">
            <p className="font-medium">{nomes[t.tabela]}</p>
            <p className="text-gray-600">{t.total ? `${t.total.toLocaleString("pt-BR")} códigos` : "Ainda não carregada"}{t.atualizado_em ? ` · ${dataBR(t.atualizado_em, true)}` : ""}</p>
            {t.fonte && <p className="text-xs text-gray-500">Fonte: {t.fonte}</p>}
            {t.erro && <p className="text-xs text-red-700">Última tentativa falhou: {t.erro}</p>}
            <Botao variante="secundario" className="mt-2" carregando={ocupado === t.tabela || t.atualizando} onClick={() => atualizar(t.tabela)}>Atualizar agora</Botao>
            {t.tabela === "cest" && (
              <div className="mt-3 border-t border-gray-200 pt-3 text-xs text-gray-600">
                <p className="font-medium text-gray-800">Se o site do CONFAZ não responder ao servidor:</p>
                <ol className="ml-4 list-decimal space-y-0.5">
                  <li>Abra <a className="text-blue-700 underline" href="https://www.confaz.fazenda.gov.br/legislacao/convenios/2018/CV142_18" target="_blank" rel="noreferrer">o Convênio ICMS 142/18 no CONFAZ</a> e espere carregar.</li>
                  <li>Aperte Ctrl+S e salve como "Página da Web, somente HTML".</li>
                  <li>Envie o arquivo aqui:</li>
                </ol>
                <label className="mt-2 inline-flex cursor-pointer items-center rounded-md border border-gray-300 bg-white px-3 py-1.5 text-sm text-gray-800 hover:bg-gray-50">
                  {ocupado === "cest" ? "Importando…" : "Importar arquivo do CONFAZ"}
                  <input type="file" accept=".html,.htm,text/html" className="hidden" disabled={ocupado === "cest"}
                    onChange={(e) => { importar(e.target.files?.[0]); e.target.value = ""; }} data-testid="cest-import-input" />
                </label>
              </div>
            )}
          </div>
        ))}
      </div>
    </Cartao>
  );
}

function Selo({ par }) {
  return <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${par[1]}`}>{par[0]}</span>;
}

export default function AdminIntegracao() {
  const [provedor, setProvedor] = useState(null);
  const [testes, setTestes] = useState({});
  const [testando, setTestando] = useState(null);
  const [empresas, setEmpresas] = useState([]);
  const [filtro, setFiltro] = useState("todas");
  const [sincronizando, setSincronizando] = useState(null);
  const [mudandoProd, setMudandoProd] = useState(null);
  // Toda mudança pede confirmação; liberar/bloquear exige motivo. "Pelo plano" desfaz a regra própria (motivo opcional).
  const [pendente, setPendente] = useState(null); // { tenant_id, modo, motivo }
  const [erro, setErro] = useState(null);

  const carregarEmpresas = useCallback(() => fiscal.adminEmpresas().then(setEmpresas).catch((e) => setErro(e.message)), []);
  useEffect(() => {
    fiscal.adminProvedor().then(setProvedor).catch((e) => setErro(e.message));
    carregarEmpresas();
  }, [carregarEmpresas]);

  const testar = async (amb) => {
    setTestando(amb);
    try {
      const r = await fiscal.adminTestarProvedor(amb);
      setTestes((t) => ({ ...t, [amb]: r }));
    } catch (e) {
      setTestes((t) => ({ ...t, [amb]: { ok: false, erro: e.message } }));
    } finally {
      setTestando(null);
    }
  };

  const sincronizar = async (tenantId) => {
    setSincronizando(tenantId);
    try {
      await fiscal.adminSincronizarEmpresa(tenantId);
    } catch (e) {
      setErro(e.message);
    } finally {
      setSincronizando(null);
      carregarEmpresas();
    }
  };

  const mudarProducao = async (tenantId, modo, motivo = "") => {
    setMudandoProd(tenantId);
    try {
      await fiscal.adminProducaoEmpresa(tenantId, modo, motivo);
      setPendente(null);
    } catch (e) {
      setErro(e.message);
    } finally {
      setMudandoProd(null);
      carregarEmpresas();
    }
  };
  const escolherModo = (l, modo) => {
    if (modo === (l.producao?.modo || "padrao")) return setPendente(null);
    return setPendente({ tenant_id: l.tenant_id, modo, motivo: "" });
  };

  const contagem = empresas.reduce((acc, x) => ({ ...acc, [x.situacao]: (acc[x.situacao] || 0) + 1 }), {});
  const lista = filtro === "todas" ? empresas : empresas.filter((x) => x.situacao === filtro);

  return (
    <div className="space-y-4">
      <h1 className="text-xl font-semibold">Integração fiscal (admin master)</h1>
      {erro && <Aviso tipo="erro">{erro}</Aviso>}

      <Cartao titulo={`Conta FSC no provedor${provedor ? ` — ${provedor.provedor}` : ""}`}
        acoes={provedor && (
          <>
            <a className="text-sm text-blue-700 underline" href={provedor.console_url} target="_blank" rel="noopener noreferrer">Console ↗</a>
            <a className="text-sm text-blue-700 underline" href={provedor.docs_url} target="_blank" rel="noopener noreferrer">Documentação ↗</a>
          </>
        )}>
        {!provedor ? <p className="text-sm text-gray-500">Carregando…</p> : (
          <div className="space-y-3">
            <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
              {provedor.ambientes.map((a) => {
                const t = testes[a.ambiente];
                return (
                  <div key={a.ambiente} className="rounded-md border border-gray-200 p-3 text-sm">
                    <div className="flex items-center justify-between">
                      <span className="font-medium">{AMBIENTE[a.ambiente]}</span>
                      <Selo par={a.configurado ? ["Credenciais no .env", "bg-emerald-100 text-emerald-800"] : ["Sem credenciais", "bg-red-100 text-red-700"]} />
                    </div>
                    <p className="mt-1 break-all text-xs text-gray-500">{a.url}</p>
                    <div className="mt-2 flex items-center gap-3">
                      <Botao variante="secundario" carregando={testando === a.ambiente} onClick={() => testar(a.ambiente)}>Testar conexão</Botao>
                      {t && (t.ok
                        ? <span className="text-emerald-700">✓ Conectado ({t.tempo_ms} ms)</span>
                        : <span className="text-red-700">{t.erro}</span>)}
                    </div>
                  </div>
                );
              })}
            </div>
            {provedor.responsavel_tecnico_configurado === false && (
              <Aviso tipo="erro">Responsável técnico não configurado: a SEFAZ rejeita NF-e/NFC-e (972). Preencha no servidor
                FISCAL_RESP_TEC_CNPJ, FISCAL_RESP_TEC_CONTATO, FISCAL_RESP_TEC_EMAIL e FISCAL_RESP_TEC_FONE (dados da FSC Sistemas) e republique.</Aviso>
            )}
            {provedor.responsavel_tecnico_configurado && (
              <p className="text-sm text-emerald-700">✓ Responsável técnico (FSC Sistemas) configurado para as notas.</p>
            )}
            <details className="rounded-md bg-gray-50 p-3 text-sm text-gray-700">
              <summary className="cursor-pointer font-medium">Como configurar a conta FSC (feito uma vez, pelo administrador)</summary>
              <ol className="mt-2 list-decimal space-y-1 pl-5">
                <li>Crie a conta da FSC Sistemas em <a className="text-blue-700 underline" href={provedor.console_url} target="_blank" rel="noopener noreferrer">console.acbr.api.br</a> e aceite as condições comerciais que chegam por e-mail.</li>
                <li>No console, crie uma credencial para <strong>homologação</strong> e outra para <strong>produção</strong>. Cada uma tem um <em>Client ID</em> e um <em>Client Secret</em>.</li>
                <li>No servidor (Emergent → variáveis de ambiente do backend), preencha
                  <code className="mx-1 rounded bg-white px-1">FISCAL_API_CLIENT_ID_HOMOLOGACAO</code>,
                  <code className="mx-1 rounded bg-white px-1">FISCAL_API_CLIENT_SECRET_HOMOLOGACAO</code> e os dois equivalentes de
                  <code className="mx-1 rounded bg-white px-1">_PRODUCAO</code>. Nunca cole esses valores no código nem nesta tela.</li>
                <li>Reinicie o backend e clique em “Testar conexão” acima.</li>
                <li>Pronto: cada cliente que salvar os dados da empresa e enviar o certificado é cadastrado automaticamente nesta conta. Acompanhe abaixo.</li>
              </ol>
            </details>
          </div>
        )}
      </Cartao>

      <Cartao titulo="Empresas dos clientes"
        acoes={
          <div className="flex gap-1 text-sm">
            {[["todas", `Todas (${empresas.length})`], ["erro", `Com erro (${contagem.erro || 0})`],
              ["pendente", `Pendentes (${contagem.pendente || 0})`], ["sincronizada", `Ativas (${contagem.sincronizada || 0})`]].map(([v, r]) => (
              <button key={v} type="button" onClick={() => setFiltro(v)}
                className={`rounded-md px-2 py-1 ${filtro === v ? "bg-blue-50 font-medium text-blue-700" : "text-gray-600 hover:bg-gray-50"}`}>{r}</button>
            ))}
          </div>
        }>
        <Tabela
          chave={(l) => l.tenant_id}
          linhas={lista}
          vazio="Nenhuma empresa nesta situação"
          colunas={[
            { id: "razao_social", rotulo: "Cliente", render: (l) => (
              <div>
                <div className="font-medium">{l.razao_social || "—"}</div>
                <div className="text-xs text-gray-500">{l.cnpj ? mascaraDocumento(l.cnpj) : "—"}{l.municipio ? ` · ${l.municipio}/${l.uf}` : ""}</div>
              </div>
            ) },
            { id: "ambiente", rotulo: "Ambiente", render: (l) => AMBIENTE[l.ambiente] || l.ambiente },
            { id: "situacao", rotulo: "Integração", render: (l) => (
              <div>
                <Selo par={SITUACAO[l.situacao]} />
                {l.erro && <div className="mt-1 max-w-xs text-xs text-red-700">{l.erro}</div>}
                {!l.erro && l.aviso_nfse && <div className="mt-1 max-w-xs text-xs text-amber-700">NFS-e: {l.aviso_nfse}</div>}
              </div>
            ) },
            { id: "producao", rotulo: "Produção", render: (l) => (
              <div data-testid={`producao-${l.tenant_id}`}>
                <span className={l.producao?.liberado ? "text-emerald-700" : "text-gray-600"}>{l.producao?.liberado ? "Liberada" : "Só homologação"}</span>
                <span className="block text-xs text-gray-500">{l.producao?.motivo}</span>
                <select value={pendente?.tenant_id === l.tenant_id ? pendente.modo : (l.producao?.modo || "padrao")} disabled={mudandoProd === l.tenant_id}
                  data-testid={`producao-modo-${l.tenant_id}`} onChange={(ev) => escolherModo(l, ev.target.value)}
                  className="mt-1 rounded-md border border-gray-300 px-2 py-1 text-xs">
                  <option value="padrao">Pelo plano (automático)</option>
                  <option value="liberado">Liberar produção</option>
                  <option value="bloqueado">Bloquear (só homologação)</option>
                </select>
                {pendente?.tenant_id === l.tenant_id && (
                  <div className="mt-2 max-w-xs space-y-1 rounded-md border border-amber-300 bg-amber-50 p-2" data-testid={`producao-confirmar-${l.tenant_id}`}>
                    <p className="text-xs font-medium text-amber-900">
                      {pendente.modo === "liberado" ? "Liberar a emissão com validade fiscal para esta empresa?"
                        : pendente.modo === "bloqueado" ? "Bloquear a produção (só homologação) para esta empresa?"
                          : "Desfazer a regra manual? A produção volta a seguir o plano da empresa."}
                    </p>
                    <input value={pendente.motivo} maxLength={300} placeholder={pendente.modo === "padrao" ? "Motivo (opcional)" : "Motivo (obrigatório)"} data-testid={`producao-motivo-${l.tenant_id}`}
                      onChange={(ev) => setPendente((p) => ({ ...p, motivo: ev.target.value }))} className="w-full rounded border border-gray-300 px-2 py-1 text-xs" />
                    <div className="flex gap-2">
                      <Botao carregando={mudandoProd === l.tenant_id} disabled={pendente.modo !== "padrao" && pendente.motivo.trim().length < 5} data-testid={`producao-ok-${l.tenant_id}`}
                        onClick={() => mudarProducao(l.tenant_id, pendente.modo, pendente.motivo.trim())}>Confirmar</Botao>
                      <Botao variante="secundario" onClick={() => setPendente(null)} data-testid={`producao-cancelar-${l.tenant_id}`}>Cancelar</Botao>
                    </div>
                  </div>
                )}
              </div>
            ) },
            { id: "sincronizada_em", rotulo: "Última sincronização", render: (l) => (l.sincronizada_em ? dataBR(l.sincronizada_em, true) : "—") },
            { id: "certificado", rotulo: "Certificado", render: (l) => (
              <span className={CERT[l.certificado]?.[1]}>
                {CERT[l.certificado]?.[0] || l.certificado}
                {l.certificado_valido_ate && <span className="block text-xs text-gray-500">até {dataBR(l.certificado_valido_ate)}</span>}
                {l.certificado !== "ausente" && !l.termo_ok && <span className="block text-xs text-amber-700">Termo pendente</span>}
              </span>
            ) },
            { id: "acoes", rotulo: "", render: (l) => (
              <Botao variante="secundario" carregando={sincronizando === l.tenant_id} onClick={() => sincronizar(l.tenant_id)}>Sincronizar de novo</Botao>
            ) },
          ]}
        />
      </Cartao>

      <TabelasFiscais />
    </div>
  );
}
